// Copyright (c) Docugami, Inc. All rights reserved.

import { IColumn, SelectionMode, TextField, IconButton, ShimmeredDetailsList, DetailsListLayoutMode } from "@fluentui/react"
import { DisabledCss, DgButton } from "components/common/DgCommonStyledComponents"
import { userFontFamily, dgFontFamily } from "components/common/Fonts"
import { OverflowTooltipText } from "components/common/OverflowText"
import { homeGray, primaryTheme } from "components/shell/presentation/AppStyles"
import { ConfirmationDialog } from "components/shell/utils/ConfirmationDialog"
import dayjs from "dayjs"
import { useDgIcons, dgIconNames } from "hooks/dgIcons"
import { ApiKey, DEFAULT_SCOPES } from "networking/tokensApi"
import { AppState } from '../../store';
import { useSelector } from 'react-redux';
import { useState, useMemo, useEffect } from "react"
import { useListKeysQuery, useAddKeyMutation, apiKeysAdapter } from "store/apiKeys/apiKeysSlice"
import { useDgAuth } from 'hooks/DgAuthentication';
import styled from "styled-components"
import { Telemetry } from 'telemetry/TelemetryManager';
import { APIKeyCopiedSchema, TelemetryEvent } from 'telemetry/TelemetryEvents';
import { ApiKeyOverflowMenu } from "./ApiKeyOverflowMenu"
import { CreateApiKeySection } from "./CreateApiKeySection"

const Root = styled.div<{ isDisabled?: boolean }>`
    display: flex;
    overflow: hidden;
    width: 100%;
    height: 100%;
    flex-direction: column;
    padding: 20px;
    box-sizing: border-box;
    ${props => !!props.isDisabled && DisabledCss};
`

const Wrapper = styled.div<{ isBusy?: boolean }>`
    cursor: ${props => !!props.isBusy ? 'progress' : 'auto'};
    width: 100%;
`

const SectionHeader = styled.div`
    display: flex;
    width: 100%;
    flex-direction: row;
    gap: 10px;
    align-items: center;
    font-size: 36px;
    font-weight: 400;
    font-family: ${userFontFamily};
    margin-bottom: 20px;
`

const SubText = styled.span`
    font-size: 20px;
    font-weight: 500;
    font-family: ${dgFontFamily};
    color: ${homeGray};
`

const ListContainer = styled.div`
    width: 100%;
    min-height: 100px;
    overflow: hidden;
    margin-bottom: 30px;
`

const CreateNewTokenButton = styled(DgButton)`
    display: flex;
    margin-left: auto;
    width: fit-content;
    font-size: 14px;
    padding: 4px 8px;
    color: ${primaryTheme.palette.white};
`

const CopyKeyContainer = styled.div`
    display: flex;
    flex-direction: row;
    overflow: hidden;
    gap: 5px;
`

const LowerContentContainer = styled.div`
    flex: 1;
`

export const DeveloperHubApiKeys = (): JSX.Element => {
    const [isCreating, setIsCreating] = useState<boolean>(false);
    const [showKey, setShowKey] = useState<boolean>(false);

    const { user } = useDgAuth();
    const username = user?.name ?? '';
    const orgName = useSelector((state: AppState) => state.organizations?.currentOrganization?.name);

    function onCopy(key: string) {
        Telemetry.trackEvent<APIKeyCopiedSchema>({
            user: username,
            workspace: orgName,
            eventName: TelemetryEvent.APIKeyCopied
        });
        navigator.clipboard.writeText(key);
    }

    const listKeysQuery = useListKeysQuery();
    const [triggerAddKey, { isLoading: isAddingKey, data: addKeyResponse, error: addKeyError }] = useAddKeyMutation();

    const apiKeys = useMemo(() => !!listKeysQuery.data ? apiKeysAdapter.getSelectors().selectAll(listKeysQuery.data) : [], [listKeysQuery.data]);

    useEffect(() => {
        if (!!addKeyResponse || !!addKeyError) {
            setShowKey(true);
        }
    }, [addKeyResponse, addKeyError]);

    useEffect(() => {
        if (!isAddingKey) {
            setIsCreating(false);
        }
    }, [isAddingKey]);

    const columns: IColumn[] = [
        {
            key: 'name',
            name: 'NAME',
            fieldName: 'name',
            minWidth: 300,
            isResizable: true,
            isRowHeader: true,
            onRender: (item: ApiKey) => {
                return <OverflowTooltipText content={item.name}>{item.name}</OverflowTooltipText>;
            }
        },
        {
            key: 'createdAt',
            name: 'DATE CREATED',
            minWidth: 150,
            isResizable: true,
            onRender: (item: ApiKey) => {
                const createdAt = dayjs(item.createdAt);
                const text = dayjs().diff(createdAt, 'hours') < 12 ?
                    createdAt.fromNow() :
                    createdAt.format('MMMM D, YYYY');

                return <OverflowTooltipText content={text}>{text}</OverflowTooltipText>;
            }
        },
        {
            key: 'overflow',
            name: '',
            isIconOnly: true,
            minWidth: 25,
            onRender: (item: ApiKey) => {
                return <ApiKeyOverflowMenu apiKey={item} />
            }
        }
    ]

    const icons = useDgIcons();
    return (
        <Wrapper isBusy={isAddingKey}>
            <Root isDisabled={isAddingKey}>
                <SectionHeader>
                    {icons.icon(dgIconNames.DgDeveloperHubTokensIcon, '', 32, 36, primaryTheme.palette.black, '')}
                    Tokens
                </SectionHeader>
                <SubText>A maximum of two tokens may be active for your workspace at a time.</SubText>
                <ListContainer>
                    <ShimmeredDetailsList
                        enableShimmer={listKeysQuery.isLoading}
                        shimmerLines={2}
                        layoutMode={DetailsListLayoutMode.justified}
                        selectionMode={SelectionMode.none}
                        columns={columns}
                        items={apiKeys}
                        styles={{
                            root: {
                                overflowY: 'hidden'
                            }
                        }}
                    />
                </ListContainer>
                <LowerContentContainer>
                    {!isCreating && !!listKeysQuery.data && listKeysQuery.data.ids.length < 2 && !showKey &&
                        <CreateNewTokenButton onClick={() => setIsCreating(true)} >
                            + CREATE NEW TOKEN
                        </CreateNewTokenButton>
                    }
                    {isCreating &&
                        <CreateApiKeySection onCreate={name => triggerAddKey({ scopes: DEFAULT_SCOPES, name: name })} onCancel={() => setIsCreating(false)} />
                    }
                </LowerContentContainer>
                <ConfirmationDialog
                    hidden={!showKey}
                    subText={!!addKeyResponse ? keyCreatedText : keyFailedText}
                    confirmButtonText="DONE"
                    dismissedCallback={() => setShowKey(false)}
                    confirmedCallback={() => setShowKey(false)}
                    dialogContentProps={{ showCloseButton: false }}>
                    {!!addKeyResponse ?
                        <CopyKeyContainer>
                            <TextField
                                readOnly
                                value={addKeyResponse!.key}
                                styles={{
                                    root: {
                                        width: '100%'
                                    }
                                }} />
                            <IconButton onClick={() => onCopy(addKeyResponse!.key!)} iconProps={{ iconName: "Copy" }} />
                        </CopyKeyContainer>
                        : undefined
                    }
                </ConfirmationDialog>
            </Root>
        </Wrapper>
    )
}

const keyCreatedText = `This token will only be shown once. Copy it and store it in a safe location.

Only use this token for your own development. Don’t share it with third-party services or applications.
`

const keyFailedText = `Something went wrong while attempting to create your key. Please try again later.`
